/**
 * ContactsContent — Account > People > Contacts subtab (ADR-141 §4).
 *
 * Directory list (name, destination summary, tag chips, source badge,
 * active state) with search + tag + source filters, an add/edit right
 * drawer (`ContactDrawer`), and row-level deactivate/reactivate.
 *
 * Filtering is client-side over the unpaginated list — the schema does
 * not declare the server's hand-rolled query params yet and directory
 * cardinality is bounded (see `contacts.service.ts` module docs).
 */
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { Badge, Box, Center, Flex, Spinner, Text } from '@chakra-ui/react';
import {
  DataTable,
  RowActionsMenu,
  SelectInput,
  StatusBadge,
  TableOptions,
  type Column,
  type ToggleOption,
} from '@themes/aegix';
import { useHasPermissions } from '../../../utils/hasPermission';
import { useContacts, usePatchContact } from '../../../hooks/queries/useContacts';
import { useContactTags } from '../../../hooks/queries/useContactTags';
import {
  summarizeDestinations,
  type Contact,
  type ContactSource,
} from '../../../services/contacts.service';
import { toaster } from '../../ui/toaster';
import { ContactDrawer } from './ContactDrawer';
import { ContactImportWizard } from './ContactImportWizard';



export function ContactsContent() {
  const { t } = useTranslation();
  const canAdd = useHasPermissions(['contacts.add_contact']);
  const canChange = useHasPermissions(['contacts.change_contact']);

  const { data: contacts = [], isLoading, error } = useContacts();
  const { data: tags = [] } = useContactTags();
  const patchContact = usePatchContact();

  const [searchValue, setSearchValue] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editContact, setEditContact] = useState<Contact | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const sourceToggleOptions: ToggleOption[] = useMemo(
    () => [
      { value: '', label: t('common.all', 'All'), 'data-e2e': 'contact-source-filter-all' },
      { value: 'manual', label: t('contacts.sourceManual', 'Manual'), 'data-e2e': 'contact-source-filter-manual' },
      { value: 'import', label: t('contacts.sourceImport', 'Import'), 'data-e2e': 'contact-source-filter-import' },
      { value: 'provider', label: t('contacts.sourceProvider', 'Provider'), 'data-e2e': 'contact-source-filter-provider' },
    ],
    [t],
  );

  const columns: Column<Contact>[] = useMemo(
    () => [
      {
        key: 'display_name',
        label: t('common.name', 'Name'),
        width: '220px',
        render: (row) => (
          <Text color="text.primary" data-e2e="contactName">
            {row.display_name}
          </Text>
        ),
      },
      {
        key: 'destinations',
        label: t('contacts.destinations', 'Destinations'),
        width: '220px',
        render: (row) => (
          <Text color="text.secondary" data-e2e="contactDestinationSummary">
            {summarizeDestinations(row.destinations)}
          </Text>
        ),
      },
      {
        key: 'tags',
        label: t('contacts.tags', 'Tags'),
        render: (row) => (
          <Flex gap={1} wrap="wrap">
            {row.tags.map((assignment) => (
              <Badge
                key={assignment.id}
                variant="subtle"
                colorPalette="blue"
                data-e2e="contactTagChip"
              >
                {assignment.tag_name}
              </Badge>
            ))}
          </Flex>
        ),
      },
      {
        key: 'source',
        label: t('contacts.source', 'Source'),
        width: '110px',
        render: (row) => (
          <Badge variant="outline" data-e2e="contactSourceBadge">
            {row.source === 'manual'
              ? t('contacts.sourceManual', 'Manual')
              : row.source === 'import'
                ? t('contacts.sourceImport', 'Import')
                : t('contacts.sourceProvider', 'Provider')}
          </Badge>
        ),
      },
      {
        key: 'is_active',
        label: t('common.status', 'Status'),
        width: '110px',
        render: (row) => (
          <StatusBadge
            status={row.is_active !== false ? 'active' : 'inactive'}
            label={t(row.is_active !== false ? 'common.active' : 'common.inactive', row.is_active !== false ? 'Active' : 'Inactive')}
          />
        ),
      },
    ],
    [t],
  );

  const filteredContacts = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    return contacts.filter((contact) => {
      if (sourceFilter && contact.source !== sourceFilter) {
        return false;
      }
      if (
        tagFilter &&
        !contact.tags.some((assignment) => String(assignment.tag) === tagFilter)
      ) {
        return false;
      }
      if (query && !contact.display_name.toLowerCase().includes(query)) {
        return false;
      }
      return true;
    });
  }, [contacts, searchValue, sourceFilter, tagFilter]);

  const tagFilterOptions = useMemo(
    () => tags.map((tag) => ({ label: tag.name, value: String(tag.id) })),
    [tags],
  );

  const openAddDrawer = () => {
    setEditContact(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (contact: Contact) => {
    setEditContact(contact);
    setDrawerOpen(true);
  };

  const toggleActive = async (contact: Contact) => {
    const activating = contact.is_active === false;
    try {
      await patchContact.mutateAsync({
        contactId: contact.id,
        body: { is_active: activating },
      });
      toaster.create({
        title: activating
          ? t('contacts.contactReactivated', 'Contact has been reactivated')
          : t('contacts.contactDeactivated', 'Contact has been deactivated'),
        type: 'success',
      });
    } catch {
      toaster.create({
        title: t('contacts.failedUpdateContact', 'Failed to update contact'),
        type: 'error',
      });
    }
  };

  return (
    <>
      <TableOptions
        toggleOptions={sourceToggleOptions}
        activeToggle={sourceFilter}
        onToggleChange={setSourceFilter}
        searchPlaceholder={t('contacts.searchPlaceholder', 'Search contacts...')}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        primaryAction={
          canAdd
            ? {
                label: t('contacts.addContact', 'Add Contact'),
                onClick: openAddDrawer,
                'data-e2e': 'addContact',
              }
            : undefined
        }
        secondaryAction={
          canAdd
            ? {
                label: t('contacts.importCsv', 'Import CSV'),
                onClick: () => setImportOpen(true),
                'data-e2e': 'importContacts',
              }
            : undefined
        }
      />

      <Box px={5} pb={5}>
        <Flex mb={3} maxW="72">
          <SelectInput
            label={t('contacts.tag', 'Tag')}
            id="contact-tag-filter"
            e2eTitle="contactTagFilter"
            option={tagFilterOptions}
            value={tagFilter}
            placeholder={t('contacts.allTags', 'All tags')}
            onChange={setTagFilter}
          />
        </Flex>

        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error
                ? error.message
                : t('contacts.failedLoadContacts', 'Failed to load contacts')}
            </Text>
          </Center>
        ) : filteredContacts.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary" data-e2e="contactsEmptyState">
              {t('contacts.noContacts', 'No contacts found')}
            </Text>
          </Center>
        ) : (
          <DataTable
            columns={columns}
            data={filteredContacts}
            keyExtractor={(row) => String(row.id)}
            onRowClick={canChange ? openEditDrawer : undefined}
            getRowProps={() => ({ 'data-e2e': 'contactRow' })}
            renderRowActions={(row) => (
              <RowActionsMenu
                actions={[
                  {
                    label: t('common.edit', 'Edit'),
                    onClick: () => openEditDrawer(row),
                    permission: canChange,
                    e2eTitle: 'editContact',
                  },
                  {
                    label:
                      row.is_active !== false
                        ? t('common.deactivate', 'Deactivate')
                        : t('common.reactivate', 'Reactivate'),
                    variant: row.is_active !== false ? 'danger' : 'default',
                    onClick: () => void toggleActive(row),
                    permission: canChange,
                    e2eTitle: 'toggleContactActive',
                  },
                ]}
              />
            )}
            caption={t('contacts.title', 'Contacts')}
          />
        )}
      </Box>

      <ContactDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        contact={editContact}
        tags={tags}
      />

      <ContactImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />
    </>
  );
}
